/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/components/ThemeSelector.tsx`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useEffect, useRef, useState } from 'react';
import { Sun, Moon, Monitor, Check, Sparkles, ChevronDown } from 'lucide-react';
import { I } from './ui';

export type SkinId = 'kontor' | 'apple' | 'mckinsey' | 'attio' | 'arta' | 'confidas';
export type ThemePref = 'light' | 'dark' | 'system';

export interface SkinMetadata {
  id: SkinId;
  name: string;
  shortName: string;
  subtitle: string;
  badge: string;
  description: string;
  recommendedMode: 'light' | 'dark';
  swatches: [string, string, string]; // [background, accent, secondary/pos]
}

export const DESIGN_SKINS: SkinMetadata[] = [
  {
    id: 'kontor',
    name: 'Kontor Classic (Swiss Gold)',
    shortName: 'Kontor Gold',
    subtitle: 'Original · Warmes Gold & Smaragd',
    badge: 'Standard',
    description: 'Klassisches Schweizer Treuhand- & Kontor-Farbschema mit warmem Gold (#e9c880 / #b87d14) und Smaragdgrün.',
    recommendedMode: 'dark',
    swatches: ['#111110', '#e9c880', '#4cc38a'],
  },
  {
    id: 'confidas',
    name: 'Confidas Treuhand (Zürich · Zug)',
    shortName: 'Confidas',
    subtitle: 'confidas.ch · Jost Italic · 0px Swiss Modernism',
    badge: 'Treuhand AG',
    description: 'Inspiriert von confidas.ch/en: Bauhaus-Typografie Jost mit kursiven Überschriften, scharfe 0px-Kanten, heller Stein (#ededed), Royal/Alpinblau (#1e3a8a) & Korallrot (#f25648).',
    recommendedMode: 'light',
    swatches: ['#ededed', '#1e3a8a', '#f25648'],
  },
  {
    id: 'apple',
    name: 'Apple Cupertino',
    shortName: 'Apple',
    subtitle: 'macOS Sequoia Glass · Squircle · System Blue',
    badge: 'Cupertino',
    description: 'Transluzentes Milchglas-Design, grosszügige Squircle-Radien (18px), SF-Pro-Typografie und Apple System Blue (#0071e3 / #2997ff).',
    recommendedMode: 'light',
    swatches: ['#f5f5f7', '#0071e3', '#30d158'],
  },
  {
    id: 'mckinsey',
    name: 'McKinsey & Strategy',
    shortName: 'McKinsey',
    subtitle: 'Deep Navy · Editorial Serif · Boardroom',
    badge: 'Executive',
    description: 'Autoritative Georgia/Bower-Serifen-Überschriften, scharfe Architektur-Kanten (3px), McKinsey Deep Navy (#051c2c) & Strategy Blue (#0065bd).',
    recommendedMode: 'light',
    swatches: ['#051c2c', '#0065bd', '#00a9f4'],
  },
  {
    id: 'attio',
    name: 'Attio Workspace',
    shortName: 'Attio',
    subtitle: '1px Hairline CRM Grid · Cobalt & Alabaster',
    badge: 'Next-Gen SaaS',
    description: 'Ultrascharfe 1px-Tabellenlinien, hohe Informationsdichte, kühles Alabaster/Carbon und Attio Cobalt (#266df0 / #4e8cff).',
    recommendedMode: 'light',
    swatches: ['#0b0d11', '#266df0', '#0f9f6e'],
  },
  {
    id: 'arta',
    name: 'Arta Finance Global',
    shortName: 'Arta Finance',
    subtitle: 'Digital Family Office · Warmstone & Forest Jade',
    badge: 'Private Wealth',
    description: 'Inspiriert von artafinance.com/global: Quiet-Luxury Warmstone (#f3f9f5), Deep Forest (#081211 / #126e6b), Luminous Jade (#46f0bd) & Champagne (#fde09c).',
    recommendedMode: 'dark',
    swatches: ['#081211', '#46f0bd', '#fde09c'],
  },
];

const store = {
  get: (k: string) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k: string, v: string) => {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* private mode */
    }
  },
};

export function getSavedSkin(): SkinId {
  const raw = store.get('kontor-skin');
  if (raw === 'kontor' || raw === 'confidas' || raw === 'apple' || raw === 'mckinsey' || raw === 'attio' || raw === 'arta') {
    return raw;
  }
  return 'kontor';
}

export function applySkinAndTheme(skin: SkinId, pref?: ThemePref) {
  const effectivePref = pref ?? ((store.get('kontor-theme') as ThemePref) || 'system');
  const dark =
    effectivePref === 'dark' ||
    (effectivePref === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.skin = skin;
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
    document.documentElement.dataset.themePref = effectivePref;
  }
  store.set('kontor-skin', skin);
  if (pref) store.set('kontor-theme', pref);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('kontor-skin-change', { detail: { skin, theme: effectivePref } }));
  }
}

export function useSkinState(externalTheme?: ThemePref, externalSetTheme?: (t: ThemePref) => void) {
  const [skin, setSkinState] = useState<SkinId>(() => getSavedSkin());
  const [internalTheme, setInternalTheme] = useState<ThemePref>(
    () => externalTheme ?? ((store.get('kontor-theme') as ThemePref) || 'system'),
  );

  const theme = externalTheme ?? internalTheme;

  useEffect(() => {
    if (typeof document !== 'undefined' && !document.documentElement.dataset.skin) {
      document.documentElement.dataset.skin = skin;
    }
    const onSync = (e: Event) => {
      const detail = (e as CustomEvent<{ skin?: SkinId; theme?: ThemePref }>).detail;
      if (detail?.skin) setSkinState(detail.skin);
      if (detail?.theme && !externalSetTheme) setInternalTheme(detail.theme);
    };
    window.addEventListener('kontor-skin-change', onSync);
    return () => window.removeEventListener('kontor-skin-change', onSync);
  }, [skin, externalSetTheme]);

  const selectSkin = (nextSkin: SkinId, switchToRecommendedMode = false) => {
    const meta = DESIGN_SKINS.find((s) => s.id === nextSkin);
    const nextTheme = switchToRecommendedMode && meta ? meta.recommendedMode : theme;
    setSkinState(nextSkin);
    if (switchToRecommendedMode && meta) {
      if (externalSetTheme) externalSetTheme(meta.recommendedMode);
      else setInternalTheme(meta.recommendedMode);
    }
    applySkinAndTheme(nextSkin, nextTheme);
  };

  const selectTheme = (nextTheme: ThemePref) => {
    if (externalSetTheme) externalSetTheme(nextTheme);
    else setInternalTheme(nextTheme);
    applySkinAndTheme(skin, nextTheme);
  };

  return { skin, theme, selectSkin, selectTheme };
}

/**
 * Single Topbar Theme & Mode Selector:
 * Compact trigger button (with 3-color swatch + popover for all 6 themes)
 * next to the single Light / Dark / System segmented toggle.
 */
export function TopbarThemeSelector({
  theme,
  setTheme,
}: {
  theme: ThemePref;
  setTheme: (t: ThemePref) => void;
}) {
  const { skin, selectSkin, selectTheme } = useSkinState(theme, setTheme);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const activeMeta = DESIGN_SKINS.find((s) => s.id === skin) ?? DESIGN_SKINS[0];

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="theme-topbar-group" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)', flexShrink: 0 }}>
      <button
        type="button"
        className="btn sm theme-trigger-btn"
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Design-Theme wechseln (Kontor, Confidas, Apple, McKinsey, Attio, Arta Finance)"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          paddingInline: '0.6rem',
          minHeight: '1.85rem',
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }} aria-hidden>
          {activeMeta.swatches.map((hex, idx) => (
            <span
              key={idx}
              style={{
                width: 9,
                height: 9,
                borderRadius: '50%',
                background: hex,
                border: '1px solid rgba(128,128,128,0.35)',
              }}
            />
          ))}
        </span>
        <span className="only-desktop theme-skin-name" style={{ fontSize: 'var(--fs-xs)' }}>
          {activeMeta.shortName}
        </span>
        <I icon={ChevronDown} sm />
      </button>

      <div className="segmented only-desktop theme-mode-seg" role="group" aria-label="Hell / Dunkel Modus">
        {(
          [
            ['light', Sun, 'Hell'],
            ['dark', Moon, 'Dunkel'],
            ['system', Monitor, 'System'],
          ] as const
        ).map(([v, ic, l]) => (
          <button key={v} aria-pressed={theme === v} title={l} aria-label={l} onClick={() => selectTheme(v)}>
            <I icon={ic} sm />
          </button>
        ))}
      </div>

      {open && (
        <div
          role="dialog"
          aria-label="Design-Theme auswählen"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 'min(23rem, 92vw)',
            background: 'var(--surface)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--r-panel)',
            boxShadow: 'var(--shadow-3)',
            padding: 'var(--sp-3)',
            zIndex: 960,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 650, fontSize: 'var(--fs-sm)' }}>
              <I icon={Sparkles} sm />
              <span>Design-Theme wählen (6 Styles)</span>
            </div>
            <div className="segmented" role="group" aria-label="Hell / Dunkel Modus">
              {(
                [
                  ['light', Sun, 'Hell'],
                  ['dark', Moon, 'Dunkel'],
                  ['system', Monitor, 'System'],
                ] as const
              ).map(([v, ic, l]) => (
                <button key={v} aria-pressed={theme === v} title={l} aria-label={l} onClick={() => selectTheme(v)}>
                  <I icon={ic} sm />
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {DESIGN_SKINS.map((item) => {
              const isSelected = item.id === skin;
              return (
                <button
                  key={item.id}
                  type="button"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 'var(--r-card)',
                    border: isSelected ? '2px solid var(--accent)' : '1px solid var(--border)',
                    background: isSelected ? 'var(--accent-soft)' : 'var(--surface-2)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                    transition: 'all 140ms ease',
                  }}
                  onClick={() => {
                    selectSkin(item.id, true);
                    setOpen(false);
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ display: 'inline-flex', gap: 3 }} aria-hidden>
                        {item.swatches.map((hex, idx) => (
                          <span
                            key={idx}
                            style={{
                              width: 11,
                              height: 11,
                              borderRadius: '50%',
                              background: hex,
                              border: '1px solid rgba(128,128,128,0.4)',
                            }}
                          />
                        ))}
                      </span>
                      <b style={{ fontSize: 'var(--fs-sm)', color: 'var(--fg)' }}>{item.name}</b>
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: 600,
                          padding: '1px 6px',
                          borderRadius: 'var(--r-pill)',
                          background: 'var(--surface)',
                          border: '1px solid var(--border)',
                          color: 'var(--fg-2)',
                        }}
                      >
                        {item.badge}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--fg-2)', marginTop: 3, lineHeight: 1.35 }}>
                      {item.description}
                    </div>
                  </div>
                  {isSelected && (
                    <span style={{ color: 'var(--accent)', flex: 'none', marginTop: 2 }}>
                      <I icon={Check} sm />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
